import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Stack, Button, Chip, Avatar, Grid, Divider, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableRow, TableCell,
  TableBody, Alert, IconButton, Tooltip,
} from '@mui/material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PersonIcon from '@mui/icons-material/Person';
import PaymentsIcon from '@mui/icons-material/Payments';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { mockOrdenesPendientes, formatCOP, formatFechaHora } from '../mock/data';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';

const tiempoTranscurrido = (iso) => {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / (60 * 1000));
  if (mins < 1) return 'recién';
  if (mins < 60) return `hace ${mins} min`;
  const horas = Math.floor(mins / 60);
  return `hace ${horas}h ${mins % 60}m`;
};

const urgenciaOrden = (iso) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / (60 * 1000));
  if (mins < 5) return { color: 'success', label: 'Nueva' };
  if (mins < 15) return { color: 'info', label: 'En espera' };
  if (mins < 30) return { color: 'warning', label: 'Demorada' };
  return { color: 'error', label: 'Muy demorada' };
};

const Ordenes = () => {
  const { notify, negocio } = useApp();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  const [ordenes, setOrdenes] = useState(mockOrdenesPendientes);
  const [detalleOrden, setDetalleOrden] = useState(null);
  const [confirmRechazo, setConfirmRechazo] = useState(null);

  const puedeRecibir = hasPermission('ordenes.recibir');
  const modoCorrecto = negocio?.modoOperacion === 'vendedor_a_caja';

  const ordenaPorTiempo = useMemo(
    () => [...ordenes].sort((a, b) => new Date(a.fechaCreacion) - new Date(b.fechaCreacion)),
    [ordenes],
  );

  const totalPendiente = ordenes.reduce((sum, o) => sum + o.total, 0);
  const cantidadItems = ordenes.reduce((sum, o) => sum + o.items.reduce((s, i) => s + i.cantidad, 0), 0);

  const cobrarOrden = (orden) => {
    // Navega al POS con la orden pre-cargada en el state
    navigate('/pos', { state: { ordenPendiente: orden } });
  };

  const rechazarOrden = () => {
    setOrdenes((prev) => prev.filter((o) => o.id !== confirmRechazo.id));
    notify(`Orden #${confirmRechazo.id} rechazada`, 'warning');
    setConfirmRechazo(null);
  };

  const refrescar = () => {
    notify('Órdenes actualizadas', 'info');
  };

  // Si no está en modo vendedor_a_caja, mostrar aviso
  if (!modoCorrecto) {
    return (
      <Box>
        <Typography variant="h4" mb={1}>Órdenes pendientes</Typography>
        <Typography color="text.secondary" mb={3}>
          Órdenes armadas por vendedores esperando cobro en caja
        </Typography>
        <Alert severity="info">
          Este módulo solo se activa cuando el negocio está en modo <strong>Vendedor a caja</strong>.
          Actualmente está en <strong>{negocio?.modoOperacion === 'multi_caja' ? 'Múltiples cajas' : 'Caja única'}</strong>,
          donde quien vende también cobra directamente en el POS.
          {' '}
          Cambia el modo en <strong>Configuración → Operación</strong> para habilitar este flujo.
        </Alert>
      </Box>
    );
  }

  if (!puedeRecibir) {
    return (
      <Box>
        <Typography variant="h4" mb={1}>Órdenes pendientes</Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          No tienes permiso para recibir órdenes de cobro.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4">Órdenes pendientes</Typography>
          <Typography color="text.secondary">
            Ventas armadas por vendedores esperando tu cobro
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refrescar}>
          Refrescar
        </Button>
      </Stack>

      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'warning.50' }}>
                  <AccessTimeIcon color="warning" />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Órdenes en cola</Typography>
                  <Typography variant="h5" fontWeight={700}>{ordenes.length}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'info.50' }}>
                  <ShoppingCartIcon color="info" />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Ítems por cobrar</Typography>
                  <Typography variant="h5" fontWeight={700}>{cantidadItems}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'success.50' }}>
                  <PaymentsIcon color="success" />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total pendiente</Typography>
                  <Typography variant="h5" fontWeight={700}>{formatCOP(totalPendiente)}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {ordenes.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <PaymentsIcon sx={{ fontSize: 72, color: 'text.secondary', opacity: 0.4 }} />
            <Typography variant="h6" mt={2}>No hay órdenes pendientes</Typography>
            <Typography color="text.secondary">
              Cuando un vendedor envíe una venta a caja aparecerá aquí.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2.5}>
          {ordenaPorTiempo.map((orden) => {
            const urgencia = urgenciaOrden(orden.fechaCreacion);
            const totalItems = orden.items.reduce((s, i) => s + i.cantidad, 0);
            return (
              <Grid item xs={12} md={6} lg={4} key={orden.id}>
                <Card sx={{
                  height: '100%',
                  borderLeft: '4px solid',
                  borderLeftColor: `${urgencia.color}.main`,
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>Orden #{orden.id}</Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center" mt={0.25}>
                          <AccessTimeIcon fontSize="small" color="action" sx={{ fontSize: 14 }} />
                          <Typography variant="caption" color="text.secondary">
                            {tiempoTranscurrido(orden.fechaCreacion)}
                          </Typography>
                        </Stack>
                      </Box>
                      <Chip label={urgencia.label} size="small" color={urgencia.color} />
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <Avatar sx={{ width: 26, height: 26, bgcolor: 'primary.light', fontSize: 12 }}>
                        {orden.vendedorNombre?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1}>
                          Vendedor
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>{orden.vendedorNombre}</Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                      <PersonIcon color="action" fontSize="small" />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1}>
                          Cliente
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>{orden.clienteNombre}</Typography>
                      </Box>
                    </Stack>

                    <Paper variant="outlined" sx={{ p: 1.25, mb: 1.5, bgcolor: 'grey.50' }}>
                      <Typography variant="caption" color="text.secondary">
                        {orden.items.length} producto{orden.items.length === 1 ? '' : 's'} · {totalItems} ítem{totalItems === 1 ? '' : 's'}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {orden.items.slice(0, 2).map((item, i) => (
                          <Typography key={i} variant="caption" display="block" sx={{
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {item.cantidad}× {item.nombre}
                          </Typography>
                        ))}
                        {orden.items.length > 2 && (
                          <Typography variant="caption" color="text.secondary" fontStyle="italic">
                            + {orden.items.length - 2} producto{orden.items.length - 2 === 1 ? '' : 's'} más
                          </Typography>
                        )}
                      </Box>
                    </Paper>

                    {orden.notas && (
                      <Alert severity="info" icon={false} sx={{ mb: 1.5, py: 0.5 }}>
                        <Typography variant="caption">{orden.notas}</Typography>
                      </Alert>
                    )}

                    <Box sx={{ flexGrow: 1 }} />

                    <Divider sx={{ my: 1.5 }} />

                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="body2" color="text.secondary">Total a cobrar</Typography>
                      <Typography variant="h5" fontWeight={700} color="primary">
                        {formatCOP(orden.total)}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined" size="small" onClick={() => setDetalleOrden(orden)}
                        startIcon={<VisibilityIcon />}
                        sx={{ flexShrink: 0 }}
                      >
                        Ver
                      </Button>
                      <Button
                        variant="contained" size="small" fullWidth
                        startIcon={<PaymentsIcon />}
                        onClick={() => cobrarOrden(orden)}
                      >
                        Cobrar
                      </Button>
                      <Tooltip title="Rechazar orden">
                        <IconButton
                          size="small" color="error"
                          onClick={() => setConfirmRechazo(orden)}
                        >
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Dialog: detalle de orden */}
      <Dialog open={Boolean(detalleOrden)} onClose={() => setDetalleOrden(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Orden #{detalleOrden?.id}
          <Typography variant="caption" color="text.secondary" display="block">
            {detalleOrden && formatFechaHora(detalleOrden.fechaCreacion)}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {detalleOrden && (
            <>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Vendedor</Typography>
                  <Typography variant="body2" fontWeight={600}>{detalleOrden.vendedorNombre}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Cliente</Typography>
                  <Typography variant="body2" fontWeight={600}>{detalleOrden.clienteNombre}</Typography>
                </Grid>
              </Grid>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell align="center">Cant.</TableCell>
                    <TableCell align="right">Precio</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detalleOrden.items.map((it, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Typography variant="body2">{it.nombre}</Typography>
                      </TableCell>
                      <TableCell align="center">{it.cantidad}</TableCell>
                      <TableCell align="right">{formatCOP(it.precioUnitario)}</TableCell>
                      <TableCell align="right">{formatCOP(it.cantidad * it.precioUnitario)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Stack spacing={0.75} sx={{ mt: 2 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Subtotal</Typography>
                  <Typography variant="body2">{formatCOP(detalleOrden.subtotal)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">IVA</Typography>
                  <Typography variant="body2">{formatCOP(detalleOrden.ivaTotal)}</Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle1" fontWeight={700}>Total</Typography>
                  <Typography variant="h6" fontWeight={700} color="primary">
                    {formatCOP(detalleOrden.total)}
                  </Typography>
                </Stack>
              </Stack>

              {detalleOrden.notas && (
                <Alert severity="info" sx={{ mt: 2 }} icon={false}>
                  <Typography variant="caption" fontWeight={600}>Notas del vendedor</Typography>
                  <Typography variant="body2">{detalleOrden.notas}</Typography>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetalleOrden(null)}>Cerrar</Button>
          <Button
            variant="contained" startIcon={<PaymentsIcon />}
            onClick={() => { const o = detalleOrden; setDetalleOrden(null); cobrarOrden(o); }}
          >
            Cobrar orden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: confirmar rechazo */}
      <Dialog open={Boolean(confirmRechazo)} onClose={() => setConfirmRechazo(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rechazar orden #{confirmRechazo?.id}</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Confirmas rechazar esta orden de <strong>{formatCOP(confirmRechazo?.total || 0)}</strong>?
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            El vendedor será notificado y deberá volver a armar la venta con el cliente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRechazo(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={rechazarOrden}>Rechazar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Ordenes;
